import { useState, useCallback, useEffect } from 'react';
import { apiCall } from '../../services/apiUtil';

// ── Shared posts logic ─────────────────────────────────────────────────────────
// All post-related state + API calls live here.
// postsPath: relative API path for GET/POST/DELETE of posts (e.g. '/social/posts')
// options.shouldLoad: auto-fetch on mount
// options.limit: default fetch limit

const usePosts = (postsPath, { shouldLoad = true, limit = 10 } = {}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(async ({ limit: fetchLimit, id_lt } = {}) => {
    if (!postsPath) return;
    try {
      setLoading(true);
      setError(null);
      let endpoint = `${postsPath}?limit=${fetchLimit || limit}`;
      if (id_lt) endpoint += `&id_lt=${id_lt}`;
      const result = await apiCall(endpoint);
      setPosts(result.success && result.data ? result.data : []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [postsPath, limit]);

  const createPost = useCallback(async (postData) => {
    try {
      const result = await apiCall(postsPath, {
        method: 'POST',
        body: JSON.stringify({
          text: postData.text || postData.content || '',
          imageUrls: postData.imageUrls || [],
          privacy: postData.privacy || 'public',
        }),
      });
      if (result.success && result.data) {
        setPosts(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      }
      throw new Error(result.message || 'Failed to create post');
    } catch (err) {
      console.error('Error creating post:', err);
      return { success: false, error: err.message };
    }
  }, [postsPath]);

  // React to a post — type is one of: like, dislike, love, haha, sad
  // Sending the same type as current reaction toggles it off.
  const reactToPost = useCallback(async (postId, type) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return { success: false };

    const prevReaction = post.userReaction;
    const isToggling = prevReaction === type;

    // Optimistic update
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const counts = { like: 0, dislike: 0, love: 0, haha: 0, sad: 0, ...(p.reactionCounts || {}), like: p.reactionCounts?.like ?? p.likes ?? 0 };
      if (prevReaction) counts[prevReaction] = Math.max(0, (counts[prevReaction] || 0) - 1);
      if (!isToggling) counts[type] = (counts[type] || 0) + 1;
      return {
        ...p,
        userReaction: isToggling ? null : type,
        isLiked: !isToggling && type === 'like',
        reactionCounts: counts,
        likes: counts.like,
      };
    }));

    try {
      if (isToggling) {
        await apiCall(`/posts/${postId}/react`, { method: 'DELETE' });
      } else {
        await apiCall(`/posts/${postId}/react`, {
          method: 'POST',
          body: JSON.stringify({ type }),
        });
      }
      return { success: true };
    } catch (err) {
      // Revert to original post state
      setPosts(prev => prev.map(p => p.id === postId ? post : p));
      return { success: false, error: err.message };
    }
  }, [posts]);

  // Delete — URL is `${postsPath}/:postId`
  const deletePost = useCallback(async (postId) => {
    try {
      await apiCall(`${postsPath}/${postId}`, { method: 'DELETE' });
      setPosts(prev => prev.filter(p => p.id !== postId));
      return { success: true };
    } catch (err) {
      console.error('Error deleting post:', err);
      return { success: false, error: err.message };
    }
  }, [postsPath]);

  // Update a single post in-place by id (e.g. after deferred media upload)
  const updatePost = useCallback((updatedPost) => {
    if (!updatedPost?.id) return;
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
  }, []);

  useEffect(() => {
    if (shouldLoad && postsPath) fetchPosts();
    else setLoading(false);
  }, [fetchPosts, shouldLoad, postsPath]);

  return { posts, loading, error, fetchPosts, createPost, reactToPost, deletePost, updatePost };
};

// ── Social feed (personal user posts via user:global) ─────────────────────────

export const useSocialFeed = (limit = 20, shouldLoad = true) => {
  const { posts, loading, error, fetchPosts, createPost, reactToPost, deletePost, updatePost } = usePosts(
    '/social/posts',
    { shouldLoad, limit }
  );
  return {
    posts, loading, error,
    hasMore: false, loadMore: () => {},
    createPost,
    reactToPost,
    deletePost,
    updatePost,
    updatePostPrivacy: async () => ({ success: false }),
    refreshFeed: fetchPosts,
  };
};

// ── Group posts (Stream Activity Feeds via Fastify) ───────────────────────────

export const useGroupPosts = (groupId, shouldLoad = true) => {
  const { posts, loading, error, fetchPosts, createPost, reactToPost, deletePost, updatePost } = usePosts(
    groupId ? `/groups/${groupId}/posts` : null,
    { shouldLoad: shouldLoad && !!groupId }
  );
  return {
    posts, loading, error,
    fetchGroupPosts: fetchPosts,
    refreshPosts: fetchPosts,
    createGroupPost: createPost,
    reactToGroupPost: reactToPost,
    deleteGroupPost: deletePost,
    updateGroupPost: updatePost,
  };
};

// ── Shared item-list logic ─────────────────────────────────────────────────────
// Handles fetch + add for comments and replies.
// path: relative API path for GET (fetch) and POST (add)
// options.shouldFetch: skip fetching when false
// options.guardSubmit: prevent concurrent POSTs (used for replies)

const useItemList = (path, { shouldFetch = true, guardSubmit = false } = {}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!path || !shouldFetch) return;
    try {
      setLoading(true);
      const result = await apiCall(path);
      setItems(result.success && result.data ? result.data : []);
    } catch (err) {
      console.error('Error fetching:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [path, shouldFetch]);

  const addItem = useCallback(async (content) => {
    if (!path) return { success: false, error: 'Missing endpoint' };
    if (guardSubmit && submitting) return { success: false, error: 'Already submitting' };
    try {
      if (guardSubmit) setSubmitting(true);
      const result = await apiCall(path, {
        method: 'POST',
        body: JSON.stringify({ text: content }),
      });
      if (result.success && result.data) {
        setItems(prev => [...prev, result.data]);
        return { success: true, data: result.data };
      }
      throw new Error(result.message || 'Failed to add');
    } catch (err) {
      console.error('Error adding:', err);
      return { success: false, error: err.message };
    } finally {
      if (guardSubmit) setSubmitting(false);
    }
  }, [path, guardSubmit, submitting]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { items, loading, submitting, fetchItems, addItem };
};

// ── Comments ──────────────────────────────────────────────────────────────────
// Works for both group and social posts — keyed by postId only.

export const usePostComments = (postId) => {
  const path = postId ? `/posts/${postId}/comments` : null;
  const { items: comments, loading, fetchItems: fetchComments, addItem: addComment } = useItemList(path);
  return { comments, loading, addComment, likeComment: () => {}, refetch: fetchComments };
};

// ── Replies ───────────────────────────────────────────────────────────────────
// Works for both group and social post comments — keyed by commentId only.

export const useCommentReplies = (commentId, postId, shouldFetch = true) => {
  const path = commentId && postId
    ? `/posts/${postId}/comments/${commentId}/replies`
    : null;
  const { items: replies, loading, submitting, fetchItems: fetchReplies, addItem: addReply } =
    useItemList(path, { shouldFetch, guardSubmit: true });
  return { replies, loading, submitting, addReply, refetch: fetchReplies };
};
